import { useState, useEffect, useMemo } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { getProducts, getCategories, createProduct, updateProduct, deleteProduct } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Image, Package, Search } from 'lucide-react';
import { ImageUploadField, getImageUrl } from '@/components/ImageUploadField';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  // Filtres
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priceSort, setPriceSort] = useState('none'); // none | asc | desc
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: '',
    category_id: '',
    image1: '',
    image2: '',
    image3: '',
    stock: '',
    featured: false,
    dimensions: '',
    material: '',
    volume_cm3: ''
  });

  useEffect(() => {
    if (localStorage.getItem('tapisstore_admin_token')) fetchData();
    else setLoading(false);
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        getProducts(),
        getCategories()
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (err) {
      if (err.response?.status !== 401) {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      price: '',
      category_id: '',
      image1: '',
      image2: '',
      image3: '',
      stock: '',
      featured: false,
      dimensions: '',
      material: '',
      volume_cm3: ''
    });
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const imgs = product.images || [];
    setFormData({
      name: product.name,
      slug: product.slug,
      description: product.description,
      price: product.price.toString(),
      category_id: product.category_id,
      image1: imgs[0] || '',
      image2: imgs[1] || '',
      image3: imgs[2] || '',
      stock: product.stock?.toString() || '0',
      featured: product.featured || false,
      dimensions: product.dimensions || '',
      material: product.material || '',
      volume_cm3: product.volume_cm3?.toString() || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        name: formData.name,
        slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-'),
        description: formData.description,
        price: parseFloat(formData.price),
        category_id: formData.category_id,
        images: [formData.image1, formData.image2, formData.image3].filter(u => u.trim()),
        stock: parseInt(formData.stock) || 0,
        featured: formData.featured,
        dimensions: formData.dimensions || null,
        material: formData.material || null,
        volume_cm3: formData.volume_cm3 ? parseInt(formData.volume_cm3) : null
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, data);
        toast.success('Produit mis à jour');
      } else {
        await createProduct(data);
        toast.success('Produit créé');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const status = error.response?.status;
      if (status !== 401 && status !== 403) toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Supprimer "${product.name}" ?`)) return;
    try {
      await deleteProduct(product.id);
      toast.success('Produit supprimé');
      fetchData();
    } catch (error) {
      const status = error.response?.status;
      if (status !== 401 && status !== 403) toast.error('Erreur lors de la suppression');
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Non catégorisé';
  };

  const filteredProducts = useMemo(() => {
    let list = [...products];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q));
    }
    if (categoryFilter !== 'all') {
      list = list.filter(p => p.category_id === categoryFilter);
    }
    if (priceSort === 'asc') list.sort((a, b) => a.price - b.price);
    if (priceSort === 'desc') list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, search, categoryFilter, priceSort]);

  return (
    <AdminLayout>
      <div data-testid="admin-products">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold">Produits</h1>
            <p className="text-muted-foreground mt-1">
              Gérez vos articles en vente
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un produit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nom *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      className="mt-1"
                      data-testid="product-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="slug">Slug</Label>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder="auto-généré si vide"
                      className="mt-1"
                      data-testid="product-slug-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    required
                    className="mt-1 min-h-[100px]"
                    data-testid="product-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Prix (€) *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      required
                      className="mt-1"
                      data-testid="product-price-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="stock">Stock</Label>
                    <Input
                      id="stock"
                      type="number"
                      min="0"
                      value={formData.stock}
                      onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                      className="mt-1"
                      data-testid="product-stock-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="category">Catégorie *</Label>
                  <Select 
                    value={formData.category_id} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="product-category-select">
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Images du produit</p>
                  <div className="space-y-3">
                    <ImageUploadField
                      label="Image 1 (principale)"
                      value={formData.image1}
                      onChange={(url) => setFormData(prev => ({ ...prev, image1: url }))}
                      fieldId="product-image-1"
                    />
                    <ImageUploadField
                      label="Image 2"
                      value={formData.image2}
                      onChange={(url) => setFormData(prev => ({ ...prev, image2: url }))}
                      fieldId="product-image-2"
                    />
                    <ImageUploadField
                      label="Image 3"
                      value={formData.image3}
                      onChange={(url) => setFormData(prev => ({ ...prev, image3: url }))}
                      fieldId="product-image-3"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dimensions">Dimensions</Label>
                    <Input
                      id="dimensions"
                      value={formData.dimensions}
                      onChange={(e) => setFormData(prev => ({ ...prev, dimensions: e.target.value }))}
                      placeholder="Ex: 200x300 cm"
                      className="mt-1"
                      data-testid="product-dimensions-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="material">Matériaux</Label>
                    <Input
                      id="material"
                      value={formData.material}
                      onChange={(e) => setFormData(prev => ({ ...prev, material: e.target.value }))}
                      placeholder="Ex: Laine, Coton"
                      className="mt-1"
                      data-testid="product-material-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="volume_cm3">Volume (cm³) — pour le calcul de livraison</Label>
                  <Input
                    id="volume_cm3"
                    type="number"
                    min="0"
                    value={formData.volume_cm3}
                    onChange={(e) => setFormData(prev => ({ ...prev, volume_cm3: e.target.value }))}
                    placeholder="Ex: 15000 (= 15L)"
                    className="mt-1"
                    data-testid="product-volume-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Volume replié/emballé du produit. Permet de calculer le colis adapté.</p>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <Switch
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, featured: checked }))}
                    data-testid="product-featured-switch"
                  />
                  <Label htmlFor="featured">Produit vedette (affiché sur la page d'accueil)</Label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-product-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Barre de recherche + filtres */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Rechercher un produit…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="products-search-input"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-44" data-testid="products-category-filter">
              <SelectValue placeholder="Toutes catégories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {categories.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={priceSort} onValueChange={setPriceSort}>
            <SelectTrigger className="w-44" data-testid="products-price-sort">
              <SelectValue placeholder="Trier par prix" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Prix : par défaut</SelectItem>
              <SelectItem value="asc">Prix : croissant</SelectItem>
              <SelectItem value="desc">Prix : décroissant</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {filteredProducts.length} produit{filteredProducts.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Products Table */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={`skeleton-${i}`} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Image</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Prix</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Vedette</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                    <TableCell>
                      {product.images?.[0] ? (
                        <img
                          key={product.images[0]}
                          src={product.images[0]}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded admin-img-fade"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                          <Image className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell>{getCategoryName(product.category_id)}</TableCell>
                    <TableCell>{product.price.toFixed(2)} €</TableCell>
                    <TableCell>
                      <span className={product.stock > 0 ? 'text-green-600' : 'text-red-600'}>
                        {product.stock}
                      </span>
                    </TableCell>
                    <TableCell>
                      {product.featured && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                          Vedette
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(product)}
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(product)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12 bg-muted/30 rounded-lg">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            {products.length === 0 ? (
              <>
                <p className="text-muted-foreground">Aucun produit pour le moment</p>
                <p className="text-sm text-muted-foreground mt-1">Commencez par ajouter votre premier produit</p>
              </>
            ) : (
              <>
                <p className="text-muted-foreground">Aucun produit ne correspond à votre recherche</p>
                <p className="text-sm text-muted-foreground mt-1">Essayez de modifier les filtres</p>
              </>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
